import { isDate } from 'es-toolkit';
import { string as yupString } from 'yup';

export type AddressValidationInput = {
  readonly addressLine1?: string | null;
  readonly addressLine2?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly postalCode?: string | null;
};

export type AddressValidationErrors = {
  readonly addressLine1?: string;
  readonly city?: string;
  readonly state?: string;
  readonly postalCode?: string;
};

export function validationForPhoneNumber(required?: boolean) {
  let validation = yupString().matches(/^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s./0-9]*$/g, 'Valid phone number required');

  if (required) {
    validation = validation.required('Phone number is required');
  }
  return validation;
}

// Accepts Date | string | null because legacy callers pass string|null from
// MUI date pickers. Only Date instances ever return true — strings always
// return false (es-toolkit's isDate). Worth tightening to a Date-only type
// guard, but the existing callers would need a refactor first.
export function isValidDate(date: Date | string | null | undefined): date is Date {
  if (!isDate(date)) return false;
  return !isNaN(date.getTime()) && date.getFullYear() > 1900;
}

export function getFormattedEOD() {
  const today = new Date();
  today.setHours(23);
  today.setMinutes(59);
  today.setSeconds(59);
  today.setMilliseconds(999);
  return today;
}

export function escapeSOQLQueryReservedCharacters(input: string): string {
  return input.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export function isValidSSN(ssn: string): boolean {
  // Remove all non-digit characters to get just the numbers
  const digitsOnly = ssn.replace(/\D/g, '');

  // Must have exactly 9 digits
  if (digitsOnly.length !== 9) {
    return false;
  }

  // Extract the three parts: area (first 3), group (middle 2), serial (last 4)
  const area = digitsOnly.slice(0, 3);
  const group = digitsOnly.slice(3, 5);
  const serial = digitsOnly.slice(5, 9);

  // First 3 digits cannot be 666, 000, or start with 9 (invalid area numbers)
  if (area === '666' || area === '000' || area.startsWith('9')) {
    return false;
  }

  // Middle 2 digits cannot be 00 (invalid group number)
  if (group === '00') {
    return false;
  }

  // Last 4 digits cannot be 0000 (invalid serial number)
  if (serial === '0000') {
    return false;
  }

  return true;
}

export function isValidZipCode(zipCode: string): boolean {
  return /^\d{5}$/.test(zipCode);
}

function normalizeAddressValue(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export function hasAddressInput(address: AddressValidationInput): boolean {
  return (
    normalizeAddressValue(address.addressLine1) !== '' ||
    normalizeAddressValue(address.addressLine2) !== '' ||
    normalizeAddressValue(address.city) !== '' ||
    normalizeAddressValue(address.state) !== '' ||
    normalizeAddressValue(address.postalCode) !== ''
  );
}

export function getAddressValidationErrors(address: AddressValidationInput): AddressValidationErrors {
  if (!hasAddressInput(address)) {
    return {};
  }

  const errors: {
    addressLine1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  } = {};

  if (normalizeAddressValue(address.addressLine1) === '') {
    errors.addressLine1 = 'Address Line 1 required';
  }

  if (normalizeAddressValue(address.city) === '') {
    errors.city = 'City required';
  }

  if (normalizeAddressValue(address.state) === '') {
    errors.state = 'State required';
  }

  const postalCode = normalizeAddressValue(address.postalCode);
  if (postalCode === '') {
    errors.postalCode = 'ZIP required';
  } else if (!isValidZipCode(postalCode)) {
    errors.postalCode = 'Please enter a valid ZIP code';
  }

  return errors;
}
